import { Connection } from 'unet'
import { THREE, ThreeView } from 'uweb/three'
import { MapView, maptalks } from 'uweb/maptalks'
import { Point, UTM, colorize } from 'uweb/utils'
import { KeyValue, Safe, Loop, Delay } from 'utils/web'

import { ViewAsPoints } from './points'
import { ViewAsSurface } from './surface'
import { ViewAsMesh } from './mesh'
import { ViewAsShots } from './csv-shots'
import { ViewAsDigs } from './dxf-digs'

import { Paner } from '../hooks/utils'
import { Init_CSS3DRenderer } from '../tools/television'

class Loader {

    cfg: iArgs
    obj: any = {}
    bnd: any = {}
    vmp: any = {}
    css: Init_CSS3DRenderer

    constructor(cfg: iArgs) {

        this.cfg = cfg
        this.css = new Init_CSS3DRenderer(cfg)

        this.vmp = {

            'ViewAsPoints': new ViewAsPoints(this.cfg),
            'ViewAsSurface': new ViewAsSurface(this.cfg),
            'ViewAsMesh': new ViewAsMesh(this.cfg),
            'csv-geojson': new ViewAsShots(this.cfg),
            'dxf-geojson': new ViewAsDigs(this.cfg),

        }

    }

    altitude = (el: number) => (this.cfg.MapView?.threeLayer.distanceToVector3(el, el)).x

    view_bound = (key: string, item: iQuery) => {

        if (item.query.Polygon) {

            if (this.bnd[key]) for (const x of this.bnd[key]) {
                this.cfg.MapView?.threeLayer.removeMesh(x)
            } else this.bnd[key] = []

            let is_3D_enabled = KeyValue('3D') === 'yes'
            let is_3D_diff = Number(KeyValue('Elevation'))

            const { x = 0, y = 0 } = item.query?.Elevation
            const unitsPerMeter = this.cfg.MapView?.threeLayer.distanceToVector3(1, 1).x

            const mi = is_3D_enabled && x > 0 ? this.altitude(x - is_3D_diff) : 0
            const ma = is_3D_enabled && y > 0 ? this.altitude(y - is_3D_diff) : 0
            const material = new THREE.LineDashedMaterial({ color: 'red', dashSize: unitsPerMeter, gapSize: unitsPerMeter })

            const top: THREE.Vector3[] = []
            const bot: THREE.Vector3[] = []
            const joint: THREE.Vector3[][] = []
            let anchor: THREE.Vector3 | null = null

            for (const [i, p] of item.query.Polygon.entries()) {
                const [nm, lt] = KeyValue('Zone').split(',')
                const ll = UTM.convertUtmToLatLng(p[0], p[1], nm, lt)
                const arg: any = { x: Number(ll.lng), y: Number(ll.lat), z: 0 }
                const f = this.cfg.MapView?.threeLayer.coordinateToVector3(arg, 0)

                // single shared anchor — locked on first vertex, XY only (z stays 0)
                if (!anchor) anchor = new THREE.Vector3(f.x, f.y, 0)

                const rx = f.x - anchor.x
                const ry = f.y - anchor.y

                top.push(new THREE.Vector3(rx, ry, mi))
                if (is_3D_enabled) {
                    bot.push(new THREE.Vector3(rx, ry, ma))
                    joint.push([
                        new THREE.Vector3(rx, ry, mi),
                        new THREE.Vector3(rx, ry, ma),
                    ])
                }
            }

            if (!anchor) return

            const addLine = (pts: THREE.Vector3[]) => {
                const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), material)
                line.position.copy(anchor!)
                line.computeLineDistances()
                this.cfg.MapView?.threeLayer.addMesh(line)
                this.bnd[key].push(line)
            }

            addLine(top)

            if (is_3D_enabled) {
                addLine(bot)
                for (const x of joint) addLine(x)
            }

            if (bot && bot.length > 0) {

                let cornerLocal = bot[0]
                for (const v of bot) if (v.x + v.y > cornerLocal.x + cornerLocal.y) cornerLocal = v
                const lift = (unitsPerMeter ?? 1) * 5    // 5 m clearance

                const cornerWorld = new THREE.Vector3(
                    anchor.x + cornerLocal.x,
                    anchor.y + cornerLocal.y,
                    anchor.z + cornerLocal.z + lift,
                )

                this.css.addTV(cornerWorld)

            }

        }

    }

    render = (key: string, item: iExplorer, cb: any = null) => {

        cb(`:⌛Loading...`)

        let started = Date.now()
        this.obj[key] = item

        const res: any = {}
        let pending = item.queries?.length

        const is_done = (qr: iQuery, view: string, rows: any) => {

            if (res.hasOwnProperty(view)) res[view].push({ qr, rows })
            else res[view] = [{ qr, rows }]

            if (--pending === 0) {

                // cb(`:✅${rows?.features.length}/${duration}s`)
                console.log(rows)
                cb(`: ${((Date.now() - started) / 1000).toFixed(1)}s`)

                for (const v in res) {

                    const View = this.vmp[v]

                    View?.addWithFilter(key, 'UTM', res[v])

                    this.obj[key].onReportCustomHandler = () => {

                        console.log(`${key} report it!`)
                        View?.report(key)

                    }

                    this.obj[key].onCloseCustomHandler = () => {

                        console.log(`${key} clear it!`)
                        View?.removeStartsWith(key)

                    }

                }

            }

        }

        if (item.queries) for (const qr of item.queries) {

            const base = { ...qr, ...qr.pane?.View }

            if (qr.query?.Polygon) this.view_bound(key, qr)
            if (!base.path) continue

            let con: Connection = this.cfg.core_collect

            if (base.channel === 'core_collect') con = this.cfg.core_collect
            else if (base.channel === 'core_data') con = this.cfg.core_data

            con.poll(base.path, { ...qr.query }, (e: any, rows: any = []) => {

                let duration = ((Date.now() - started) / 1000).toFixed(1)

                if (e?.response?.data) this.cfg.messageApi.open({ key, type: 'warn', content: e.response.data })
                if (e) { cb(`:⛔${duration}s`) } else {

                    if (Array.isArray(rows)) cb(`:✅${rows.length}/${duration}s`)
                    else if (rows?.features) cb(`:✅${rows?.features.length}/${duration}s`)

                    is_done(qr, base.view, rows)

                }

            })

        }

    }

    report = (key: string, item: iExplorer) => {

        console.log(key, item)
        if (this.obj[key]?.onReportCustomHandler) this.obj[key].onReportCustomHandler()
        // delete this.obj[key]

    }

    close = (key: string, item: iExplorer) => {

        if (this.obj[key]?.onCloseCustomHandler) this.obj[key].onCloseCustomHandler()
        delete this.obj[key]

    }

}

export class ExploreManager {

    Maptalks: MapView
    cfg: iArgs
    tool: any
    pane = new Paner({ container: document.getElementById('pane') || undefined, theme: 'light' })
    loader: Loader
    items: iExplorer[] = []
    queue: [iExplorer, any][] = []

    constructor(Maptalks: MapView, cfg: iArgs, message: any) {

        this.Maptalks = Maptalks
        this.cfg = cfg
        this.loader = new Loader(cfg)

        this.items = JSON.parse(KeyValue(`Explorer`) ?? '[]')

        const previews: any = [
            {
                name: 'Base Map',
                queries: [
                    {
                        pane: {
                            'Base tile': KeyValue('Base tile') === 'true',
                            'Geofences': KeyValue('Geofences') === 'true',
                            '3D': KeyValue('3D') === 'yes',
                            'Elevation': Number(KeyValue('Elevation')) || 1500, '_Elevation': { min: 1000, max: 2000, step: 10 },
                            'Last View': KeyValue('Last View') === 'true',
                        }
                    }
                ]
            }
        ]

        this.update([...previews, ...this.items])

        cfg.event.on('explorer', (e: iExplorer) => {

            this.items.push(e)
            KeyValue('Explorer', JSON.stringify(this.items))
            this.update([e], true)

        })

        this.queloader()

    }

    queloader = () => {

        let isFree = true

        Loop(() => {

            if (isFree && this.queue.length > 0) {

                const p = this.queue.shift()
                if (p) {

                    const [item, tab] = p

                    this.loader.render(item.name, item, (status: string) => {

                        tab.title = `${item.name}${status}`
                        isFree = true

                    })

                }

            }

        }, 50)

    }

    update = (list: iExplorer[], expand = false) => {

        for (const [i, e] of list.entries()) {

            const multi = e.queries?.length
            const _pane = this.pane.addFolder({ title: e.name, expanded: expand, disabled: false })
            const is_not_base = e.name !== 'Base Map'

            e.queries?.forEach((qr: iQuery, idx: number) => {

                const _tab = multi > 1 ? _pane.addFolder({ title: qr.title ?? `${e.name}-${idx}`, expanded: true, disabled: false }) : _pane

                if (qr.query) for (const x in qr.query) {

                    if (qr.hide?.indexOf(x) !== -1) continue
                    _tab.addInput(qr.query, x)

                }

                if (qr.pane) for (const x in qr.pane) {

                    if (x[0] === '_') continue

                    if (qr.pane[`_${x}`] && Array.isArray(qr.pane[`_${x}`])) {

                        const init = qr.pane[x]
                        const list = qr.pane[`_${x}`]

                        _tab.addBlade({ view: 'list', label: x, options: list.map((v: any) => ({ text: v.name, value: v })), value: list[init.id] })
                            .on('change', (v: any) => { qr.pane[x] = v.value })

                    }
                    else if (qr.pane[`_${x}`]) _tab.addInput(qr.pane, x, qr.pane[`_${x}`])
                    else _tab.addInput(qr.pane, x)

                }

                if (is_not_base) {

                    _tab.on('change', (v: any) => {

                        this.items.forEach((_, idx) => { if (_.name === e.name) this.items[idx] = e })
                        KeyValue('Explorer', JSON.stringify(this.items))
                        this.loader.view_bound(e.name, qr)

                    })

                    if (multi === idx + 1) {

                        _pane.addButton({ label: '', title: '✨ AI Scan' }).on('click', () => {

                            this.loader.report(e.name, e)

                        })
                        _pane.addButton({ label: '', title: '⬇ Load' }).on('click', () => { this.queue.push([e, _pane]) })
                        _pane.addButton({ label: '', title: '✖ Close' }).on('click', () => {

                            this.items = this.items.filter((_, idx) => _.name !== e.name)
                            KeyValue('Explorer', JSON.stringify(this.items))
                            this.loader.close(e.name, e)
                            _pane.dispose()

                        })

                    }

                } else {

                    _tab.addButton({ label: '', title: 'Reset' }).on('click', () => {
                        KeyValue('Explorer', '[]')
                        location.reload()
                    })

                    _tab.on('change', ({ presetKey, value }: any) => {

                        if (presetKey === '3D') value = value === true ? 'yes' : 'no'
                        KeyValue(presetKey, `${value}`)
                        this.cfg.messageApi.open({ key: '_reload_warning_', type: 'info', content: `${presetKey}: ${value} this may require reload to take effect!` })

                    })

                }

            })

            is_not_base && this.queue.push([e, _pane])

        }

    }

}